import {
  deleteV1CompaniesCompanyIdUsersId,
  deleteV1CompaniesCompanyIdUsersUserIdSessions,
  getV1CompaniesCompanyIdUsers,
  GetV1CompaniesCompanyIdUsersData,
  getV1Users,
  GetV1UsersData,
  patchV1CompaniesCompanyIdUsersId,
  postV1CompaniesCompanyIdUsers,
  postV1CompaniesCompanyIdUsersUserIdImpersonations,
  User_Create_In_Company,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { UserNotificationsPreferenceDTO } from '~components/UserForm/UserNotificationsPreferenceForm';
import { SESSION_JWT_HEADER } from '~constants/consts';
import { AdminUser } from '~hooks/admin/useUsersAdmin/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { startImpersonation } from '~services/sessionTokens';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface GetAllUsersParams {
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  searchQuery?: string;
  signal?: AbortSignal;
}

interface GetCompanyUsersParams extends GetAllUsersParams {
  companyId: string;
}

export const useUsersAdmin = () => {
  const { userStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);

  const getCompanyUsers = useCallback(
    async ({
      companyId,
      cursor,
      limit,
      link,
      searchQuery,
      signal,
    }: GetCompanyUsersParams) => {
      const query: GetV1CompaniesCompanyIdUsersData['query'] = {
        'page[limit]': limit,
        ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        ...(searchQuery?.length ? { 'search[datagrid]': searchQuery } : {}),
      };

      const response = await getV1CompaniesCompanyIdUsers({
        path: { 'company-id': companyId },
        query,
        signal,
      });

      return {
        data: response.data.data.map((user) => AdminUser.parse(user)),
        pagination: extractPagination(response),
      };
    },
    [],
  );

  const getAllUsers = useCallback(
    async ({ cursor, limit, link, searchQuery, signal }: GetAllUsersParams) => {
      const query: GetV1UsersData['query'] = {
        'page[limit]': limit,
        ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        ...(searchQuery?.length ? { 'search[datagrid]': searchQuery } : {}),
      };

      const response = await getV1Users({ query, signal });

      return {
        data: response.data.data.map((user) => AdminUser.parse(user)),
        pagination: extractPagination(response),
      };
    },
    [],
  );

  const updateUser = async (
    userData: AdminUser,
    userNotificationsPreferenceData?: UserNotificationsPreferenceDTO,
    companyId?: string,
  ) => {
    try {
      const response = await patchV1CompaniesCompanyIdUsersId({
        path: { 'company-id': companyId || effectiveCompanyId, id: userData.id },
        body: AdminUser.deparse(userData, undefined, userNotificationsPreferenceData),
      });
      const user = AdminUser.parse(response.data.data);

      return user;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.users.failed_to_update'),
        [422],
      );
    }
  };

  const impersonateUser = async (uuid: string, companyId: string) => {
    try {
      const response = await postV1CompaniesCompanyIdUsersUserIdImpersonations({
        path: { 'company-id': companyId, 'user-id': uuid },
      });
      const impersonationToken = response.headers?.[SESSION_JWT_HEADER];

      if (!impersonationToken) {
        throw new Error('Impersonation response carried no session token');
      }

      // reloads: the impersonated session is hydrated by the app's normal boot
      // path rather than patched into the running one.
      startImpersonation(impersonationToken, userStore.user.fullName);
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.users.failed_to_impersonate'),
        );
      } catch {
        // Swallow to avoid unhandled promise rejection.
      }
    }
  };

  const createUser = async (
    userData: AdminUser,
    invitesToSend: User_Create_In_Company['send_invite'] = {},
  ) => {
    try {
      const response = await postV1CompaniesCompanyIdUsers({
        path: { 'company-id': userData.company?.id || effectiveCompanyId },
        body: AdminUser.deparseForCompanyCreate(userData, invitesToSend),
      });
      const user = AdminUser.parse(response.data.data);

      return user;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.users.failed_to_create'),
        [422],
      );
    }
  };

  const deleteUser = async (id: string, companyId?: string) => {
    try {
      await deleteV1CompaniesCompanyIdUsersId({
        path: { 'company-id': companyId || effectiveCompanyId, id },
      });
      return true;
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.users.failed_to_deactivate'),
        );
      } catch {
        // Error already surfaced via toast and Sentry by handleRequestError.
      }

      return false;
    }
  };

  const revokeAllUserSessions = async (id: string, companyId: string) => {
    try {
      await deleteV1CompaniesCompanyIdUsersUserIdSessions({
        path: { 'company-id': companyId, 'user-id': id },
      });
    } catch (error) {
      connection.handleRequestError(error);
    }
  };

  return {
    createUser,
    deleteUser,
    getAllUsers,
    getCompanyUsers,
    impersonateUser,
    revokeAllUserSessions,
    updateUser,
  } as const;
};
